import { getRoot, Instance, types } from 'mobx-state-tree';
import { DYNAMIC_MODAL_GROUPS, TOAST_TYPES } from '../toast/toastStore.types';
import { IRootStore } from '../store';
import { ToastProps } from '@photosi/rn-components/toast/Toast.types';
import { TEXT_ICON_BUTTON_TYPES } from '@photosi/rn-components/buttons/text-icon-button/TextIconButton.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';

export const ToastModel = types
  .model({
    group: types.enumeration(Object.values(DYNAMIC_MODAL_GROUPS)),
    id: types.optional(types.string, () => new Date().toISOString()),
    icon: types.maybe(types.string),
    iconColor: types.maybe(types.string),
    message: types.string,
    ctaText: types.maybe(types.string),
    duration: types.maybe(types.number),
    type: types.optional(types.enumeration(Object.values(TOAST_TYPES)), TOAST_TYPES.Info),
    removeOnPress: types.optional(types.boolean, false),
    alreadyExists: types.optional(types.boolean, false), // used to check if the toast already exists. i.e. alert toast
    isExiting: types.optional(types.boolean, false), // used to trigger the exit animation
    loader: types.optional(types.boolean, false),
    ctaIconType: types.optional(types.string, TEXT_ICON_BUTTON_TYPES.ICON_RIGHT),
    ctaIconName: types.optional(types.string, ICON_NAMES.ArrowRight1),
    filled: types.optional(types.boolean, false),
    animatedIcon: types.maybe(types.string),
  })
  .volatile(() => ({
    onPress: () => {},
    exitTimeout: null as NodeJS.Timeout | null,
    onPressClose: undefined as (() => void) | undefined,
    onAfterClose: undefined as (() => void) | undefined,
  }))
  .views(self => ({
    get root(): IRootStore {
      return getRoot(self);
    },
  }))
  .actions(self => ({
    // this function is used to clear the exit timeout, is moved into a separate action to be used inside the showToast action
    clearExitTimeout() {
      if (self.exitTimeout) {
        clearTimeout(self.exitTimeout);
        self.exitTimeout = null;
      }
    },
  }))

  .actions(self => ({
    setOnPress(onPress: () => void) {
      self.onPress = onPress;
    },

    setExitTimeout(callback: () => void, duration: number | undefined) {
      self.clearExitTimeout();
      if (typeof duration === 'number' && duration > 0) {
        self.exitTimeout = setTimeout(callback, duration);
      }
    },

    setIsExiting(value: boolean) {
      self.isExiting = value;
    },
  }))
  .actions(self => ({
    attachEventHandlers(toast: ToastProps) {
      if (toast.onPress) {
        self.setOnPress(() => {
          toast.onPress!();
          if (toast.removeOnPress && self.root.toastStore) {
            self.root.toastStore.removeToast(self.id);
          }
        });
      }

      // set the exit timeout
      self.setExitTimeout(() => {
        self.setIsExiting(true);
      }, self.duration);

      if (toast.onPressClose) {
        self.onPressClose = toast.onPressClose;
      }

      if (toast.onAfterClose) {
        self.onAfterClose = toast.onAfterClose;
      }
    },
  }))
  .actions(self => ({
    update(toast: ToastProps) {
      self.group = toast.group ?? DYNAMIC_MODAL_GROUPS.Generic_Toast;
      self.id = toast.id || new Date().toISOString();
      self.message = toast.message || '';
      self.icon = toast.icon;
      self.ctaText = toast.ctaText;
      self.duration = toast.duration; // the toast animation on exit is long 2sec
      self.type = toast.type ?? TOAST_TYPES.Info;
      self.removeOnPress = !!toast.removeOnPress;
      self.alreadyExists = !!toast.alreadyExists;
      self.isExiting = !!toast.isExiting;

      self.attachEventHandlers(toast);
    },
  }));

export type ToastInstance = Instance<typeof ToastModel>;
